import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import "./Home.css";
import {
  heroSliderAPI,
  portalAPI,
  teamAPI,
  aboutAPI,
  footerAPI,
} from "../services/api";
import thtmglogo from "../assets/thtmglogo.png";

// Homepage will try to load CMS content; fall back to built-in examples if CMS empty or unavailable.
const defaultSlides = [
  {
    bg: "https://images.unsplash.com/photo-1544735716-392fe2489ffa?auto=format&fit=crop&w=1920&q=80",
    title: "SaffronPages",
    desc: "Discover spiritual wisdom and connect with divine knowledge",
    link: "https://saffronpages.com/en/in-gn",
    buttonText: "Explore SaffronPages",
  },
  {
    bg: "https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1920&q=80",
    title: "The Hindu Today",
    desc: "Your daily dose of sacred news and spiritual insights",
    link: "/the-hindu-today1",
    buttonText: "Visit The Hindu Today",
  },
  {
    bg: "https://images.unsplash.com/photo-1519125323398-675f0ddb6308?auto=format&fit=crop&w=1920&q=80",
    title: "Digital Temples",
    desc: "Experience the divine from the comfort of your home",
    link: "https://digitaltemples.com/",
    buttonText: "Enter Digital Temples",
  },
];

const defaultPortals = [];
const defaultTeam = [];

const useAnimateOnScroll = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    const elements = document.querySelectorAll(".scroll-animate");
    elements.forEach((el) => observer.observe(el));
    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);
};

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slides, setSlides] = useState(defaultSlides);
  const [autoPlay, setAutoPlay] = useState(true);

  useEffect(() => {
    let mounted = true;
    heroSliderAPI
      .getAll()
      .then((res) => {
        if (!mounted) return;
        if (Array.isArray(res.data) && res.data.length) {
          // backend stores image paths like /uploads/..., ensure full URL if needed
          const normalized = res.data.map((s) => ({
            bg: s.imageUrl
              ? `${process.env.REACT_APP_API_URL}${s.imageUrl}`
              : s.bg || defaultSlides[0].bg,
            title: s.title || "",
            desc: s.subtitle || "",
            link: s.buttonLink || "#",
            buttonText: s.buttonText || "",
          }));
          setSlides(normalized);
        }
      })
      .catch(() => {
        // keep defaults on error
      });

    return () => {
      mounted = false;
    };
  }, []);

  // Auto-play effect
  useEffect(() => {
    if (!autoPlay || slides.length === 0) return;

    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [autoPlay, slides.length]);

  const goToPrevious = () => {
    setAutoPlay(false);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToNext = () => {
    setAutoPlay(false);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const handleDotClick = (index) => {
    setAutoPlay(false);
    setCurrentSlide(index);
  };

  return (
    <section className="h-screen relative overflow-hidden">
      {slides.map((slide, index) => (
        <div
          key={index}
          className={`absolute w-full h-full bg-cover bg-center transition-opacity duration-1000 ease-in-out ${index === currentSlide ? "opacity-100" : "opacity-0"}`}
          style={{ backgroundImage: `url('${slide.bg}')` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[rgba(139,0,0,0.7)] to-[rgba(255,107,53,0.5)]"></div>
          <div className="relative z-[2] h-full flex items-center justify-center text-center text-white max-w-4xl mx-auto px-5">
            <div
              className="animate-slideUp"
              style={{
                animationDelay: `${index === currentSlide ? "0s" : "1s"}`,
              }}
            >
              <h1 className="font-cinzel text-4xl md:text-6xl mb-5 [text-shadow:2px_2px_4px_rgba(0,0,0,0.5)]">
                {slide.title}
              </h1>
              <p className="text-xl md:text-2xl mb-8 [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)] animate-slideUp [animation-delay:0.2s]">
                {slide.desc}
              </p>
              <NavLink
                to={slide.link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold py-4 px-10 rounded-full text-lg shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-[0_15px_35px_rgba(255,107,53,0.5)] animate-slideUp [animation-delay:0.4s]"
              >
                {slide.buttonText || `Visit ${slide.title}`}
              </NavLink>
            </div>
          </div>
        </div>
      ))}
      {/* Left Arrow */}
      <button
        onClick={goToPrevious}
        className="absolute left-5 top-1/2 -translate-y-1/2 z-[3] bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      </button>
      {/* Right Arrow */}
      <button
        onClick={goToNext}
        className="absolute right-5 top-1/2 -translate-y-1/2 z-[3] bg-white/20 hover:bg-white/40 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 backdrop-blur-sm"
      >
        <svg
          className="w-6 h-6"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      </button>
      {/* Dots Navigation */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-4 z-[3]">
        {slides.map((_, index) => (
          <div
            key={index}
            onClick={() => handleDotClick(index)}
            className={`w-4 h-4 rounded-full cursor-pointer transition-all duration-300 ${index === currentSlide ? "bg-[#FF6B35] scale-125" : "bg-white/50"}`}
          ></div>
        ))}
      </div>
    </section>
  );
};

const LocationSEO = () => (
  <section className=" py-12 sm:py-20 px-5 bg-gradient-to-br from-[#FDF5E6] to-[#FAEBD7] text-center relative overflow-hidden border-y border-red-800/10">
    <div className="max-w-7xl mx-auto">
      <h2 className="section-title font-cinzel text-3xl md:text-5xl text-[#FF6B35] mb-16 relative animate-pulse-om">
        Find Sacred Places Near You
      </h2>
      <p className="text-lg text-gray-600 -mt-10 mb-12 max-w-3xl mx-auto">
        Discover temples, events, and community services tailored to your city
      </p>
      <div className="max-w-2xl mx-auto">
        <div className="relative flex flex-col md:flex-row items-center bg-white/30 backdrop-blur-lg rounded-xl md:rounded-full border border-white/40 shadow-xl p-2 transition-all duration-300 focus-within:shadow-2xl focus-within:-translate-y-1">
          <input
            type="text"
            placeholder="Search or select your city..."
            className="w-full md:w-auto flex-grow py-3 px-6 bg-transparent text-lg placeholder-gray-600/60 outline-none rounded-full"
          />
          <button className="w-full mt-2 md:mt-0 md:w-auto bg-gradient-to-r from-[#FF6B35] to-[#F7931E] text-white font-semibold py-3 px-8 rounded-full text-lg flex items-center justify-center gap-2 transition-transform duration-300 hover:scale-105">
            <i className="fas fa-map-marker-alt"></i> Find City
          </button>
        </div>
      </div>
    </div>
  </section>
);

const AdvancedCards = () => {
  const [portals, setPortals] = React.useState(defaultPortals);
  React.useEffect(() => {
    let mounted = true;
    portalAPI
      .getAll()
      .then((res) => {
        if (!mounted) return;
        if (Array.isArray(res.data) && res.data.length) {
          const normalized = res.data.map((p, i) => ({
            icon: p.imageUrl
              ? `${process.env.REACT_APP_API_URL}${p.imageUrl}`
              : "🌐",
            number: p.order
              ? String(p.order).padStart(2, "0")
              : String(i + 1).padStart(2, "0"),
            title: p.title || "",
            description: p.description || "",
            features: p.features || [],
            link:
              p.link === "https://thtmg.com/the-hindu-today1"
                ? "the-hindu-today"
                : p.link || "#",
          }));
          setPortals(normalized);
        }
      })
      .catch((err) => {
        console.log("Error loading portals:", err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className=" py-12 sm:py-16 px-5 bg-gradient-to-br from-[#FFF8DC] via-[#FFFACD] to-[#F5DEB3]">
      <div className="max-w-7xl mx-auto">
        <h2 className="section-title font-cinzel text-3xl md:text-5xl text-[#8B0000] mb-10 relative animate-pulse-om">
          Our Sacred Digital Portals
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {portals.map((card, idx) => (
            <div
              key={idx}
              className="group relative h-[480px] bg-white/50 rounded-3xl border border-white/60 p-8 overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-4 hover:shadow-2xl shadow-orange-500/20 flex flex-col"
            >
              <div
                className="absolute top-[-2px] left-[-2px] w-[calc(100%+4px)] h-[calc(100%+4px)] animate-gradientRotate bg-gradient-to-r from-[#FF6B35] via-[#8B0000] to-[#DAA520] [background-size:400%] rounded-[26px] opacity-60 sm:opacity-0 sm:group-hover:opacity-60
 transition-opacity duration-500 -z-10"
              ></div>
              <div className="flex justify-between items-start">
                <div className="relative w-20 h-20 flex items-center justify-center">
                  <div className="absolute w-20 h-20 border-2 border-orange-500/30 rounded-full animate-rotateClockwise"></div>
                  <div className="absolute w-16 h-16 border border-red-800/40 rounded-full animate-rotateCounterClockwise"></div>
                  <img
                    src={card.icon}
                    alt={card.title}
                    className="w-16 h-16 rounded-full object-cover z-10 transition-transform duration-500 group-hover:scale-125"
                  />
                </div>
                <div className="font-cinzel text-5xl font-bold text-[#8B0000]/20 transition-all duration-500 group-hover:text-[#DAA520]/50 group-hover:scale-110 group-hover:rotate-12">
                  {card.number}
                </div>
              </div>
              <div className="relative mt-4">
                <h3 className="font-cinzel line-clamp-2 text-2xl mb-4 transition-colors duration-500 text-[#FF6B35] sm:text-[#8B0000] sm:group-hover:text-[#FF6B35]">
                  {card.title || `Portal ${idx + 1}`}
                </h3>
                <p className="text-[#7A5C3E] leading-relaxed mb-6 h-32 transition-colors duration-500 group-hover:text-[#8B0000] overflow-auto advance-scrollbar">
                  {card.description || ""}
                </p>
              </div>
              <div className="mt-auto">
                {/* Feature tags */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {(card.features || []).map((tag) => (
                    <span
                      key={tag}
                      className="bg-[#DAA520]/10 border border-[#DAA520]/30 text-[#8B0000] text-xs font-medium px-3 py-1 rounded-full transition-all duration-300 group-hover:bg-[#DAA520]/20 group-hover:-translate-y-1"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <a
                  href={card.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex justify-between items-center bg-gradient-to-r from-red-800 to-orange-500 text-white font-semibold py-4 px-6 rounded-full shadow-lg transition-all duration-300 group-hover:from-orange-500 group-hover:to-yellow-500 group-hover:scale-105"
                >
                  Explore
                  <i className="fas fa-arrow-right transition-transform duration-300 group-hover:translate-x-1 group-hover:rotate-45"></i>
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const About = () => {
  const [about, setAbout] = React.useState({});
  React.useEffect(() => {
    let mounted = true;
    aboutAPI
      .get()
      .then((res) => {
        if (!mounted) return;
        if (res.data) setAbout(res.data);
      })
      .catch((err) => {
        console.log("Error loading about:", err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className=" py-12 sm:py-16 px-5 bg-gradient-to-br from-[#FDF5E6] to-[#FAEBD7]">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="section-title font-cinzel text-3xl md:text-5xl text-red-800 mb-8 relative animate-pulse-om">
          {about.title || "About Us"}
        </h2>
        <div className="flex flex-col md:flex-row items-center gap-12">
          <img
            src={
              about.imageUrl
                ? `${process.env.REACT_APP_API_URL}${about.imageUrl}`
                : "https://via.placeholder.com/256"
            }
            alt="About"
            className="w-64 h-64 rounded-full object-cover shadow-lg"
          />
          <div className="text-left md:text-left">
            <p className="text-lg text-gray-600 leading-relaxed ">
              {about.content || "Learn more about our mission and vision."}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Team = () => {
  const [team, setTeam] = React.useState(defaultTeam);
  const [activeTab, setActiveTab] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    teamAPI
      .getAll()
      .then((res) => {
        if (!mounted) return;
        if (Array.isArray(res.data) && res.data.length) {
          const normalized = res.data.map((m) => ({
            avatar: m.imageUrl
              ? `${process.env.REACT_APP_API_URL}${m.imageUrl}`
              : m.avatar || "https://via.placeholder.com/150",
            name: m.name,
            role: m.role || "",
            bio: m.bio || "",
            order: m.order ?? 0,
            departmentId: m.departmentId ?? 0,
            departmentName: (m.departmentName || "").trim(),
          }));
          setTeam(normalized);
        }
      })
      .catch((err) => {
        console.log("Error loading team:", err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  // Unique departmentName se tabs, departmentId ke order mein
  const tabs = React.useMemo(() => {
    const map = new Map();
    team.forEach((m) => {
      if (m.departmentName && !map.has(m.departmentName)) {
        map.set(m.departmentName, m.departmentId);
      }
    });
    return [...map.entries()].sort((a, b) => a[1] - b[1]).map(([name]) => name);
  }, [team]);

  const currentTab =
    activeTab && tabs.includes(activeTab) ? activeTab : tabs[0];

  const filteredTeam = React.useMemo(
    () =>
      team
        .filter((m) => m.departmentName === currentTab)
        .sort((a, b) => a.order - b.order),
    [team, currentTab],
  );

  return (
    <section className="py-12 sm:py-16 px-5 bg-gradient-to-br from-[#FDF5E6] to-[#FAEBD7]">
      <div className="max-w-7xl mx-auto text-center">
        <h2 className="section-title font-cinzel text-3xl md:text-5xl text-red-800 mb-4 relative animate-pulse-om">
          Meet Our Dedicated Team
        </h2>
        <p className="text-lg text-gray-600 mb-4 max-w-3xl mx-auto">
          The devoted souls behind Sanatan Digital's mission
        </p>

        {/* Tabs */}
        {tabs.length > 0 && (
          <div className="border-b border-red-800/10 mb-[30px]">
            <div
              role="tablist"
              aria-label="Team categories"
              className="flex w-fit max-w-full mx-auto overflow-x-auto gap-[8px] sm:gap-[16px] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {tabs.map((tab) => {
                const isActive = currentTab === tab;
                return (
                  <button
                    key={tab}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setActiveTab(tab)}
                    className={`shrink-0 whitespace-nowrap px-[20px] py-[12px] text-[15px] font-medium border-b-2 transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/40 rounded-t-[8px] ${
                      isActive
                        ? "border-orange-500 text-red-800"
                        : "border-transparent text-gray-500 hover:text-red-800 hover:bg-white/40"
                    }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {filteredTeam.length === 0 ? (
          <p className="text-gray-500 text-[15px] py-[40px]">
            No team members found in this category.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {filteredTeam.map((member, index) => (
              <div
                key={`${currentTab}-${index}`}
                className="group relative bg-white/20 backdrop-blur-lg rounded-2xl border border-white/30 py-8 px-4 text-center shadow-lg transition-all duration-300 hover:-translate-y-2 hover:shadow-orange-500/20 flex flex-col"
              >
                <img
                  src={member.avatar}
                  alt={member.name}
                  className="w-32 h-32 rounded-full object-cover mx-auto mb-5 border-4 border-orange-500 shadow-xl transition-all duration-300 group-hover:scale-105 group-hover:rotate-3"
                />

                <h3 className="font-cinzel text-2xl text-red-800 mb-2 line-clamp-2">
                  {member.name}
                </h3>

                <p className="text-md font-semibold text-orange-500 mb-4">
                  {member.role}
                </p>

                <p className="text-gray-600 text-sm leading-relaxed mb-2 h-[160px] overflow-auto advance-scrollbar">
                  {member.bio}
                </p>

                <div className="mt-auto flex justify-center gap-4 text-2xl text-red-800">
                  <a
                    href="#"
                    className="transition-transform duration-300 hover:text-orange-500 hover:-translate-y-1"
                  >
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                  <a
                    href="#"
                    className="transition-transform duration-300 hover:text-orange-500 hover:-translate-y-1"
                  >
                    <i className="fab fa-twitter"></i>
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const Footer = () => {
  const [footer, setFooter] = React.useState(null);
  React.useEffect(() => {
    let mounted = true;
    footerAPI
      .get()
      .then((res) => {
        if (!mounted) return;
        if (res.data) setFooter(res.data);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const companyName = footer?.companyName || "Sanatan Digital";
  const description =
    footer?.description ||
    "Connecting hearts to the divine through digital innovation.";

  return (
    <footer
      id="footer"
      className=" bg-white text-white relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 w-full overflow-hidden leading-none rotate-180">
        {/* <svg
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
          className="relative block w-full h-[60px]"
          style={{ fill: "#FFFACD" }}
        >
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"></path>
        </svg> */}
      </div>
      <div className="relative pt-8 pb-8 max-w-7xl mx-auto px-5">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-4 mb-6">
              <img
                src={thtmglogo}
                alt="thtmglogo"
                className="w-[167px] mb-[0px]"
              />
              <h3 className=" text-orange-500 text-xl  ">{companyName}</h3>
            </div>
            <p className="text-lg text-white/90 leading-relaxed mb-6">
              {description}
            </p>
            <div className="bg-orange-500/10 p-5 rounded-lg border-l-4 border-orange-500 mb-6">
              <p className="font-noto-devanagari text-lg font-semibold text-yellow-500 mb-2">
                "सर्वे भवन्तु सुखिनः"
              </p>
              <p className="italic text-gray-900">"May all beings be happy"</p>
            </div>
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-cinzel text-orange-500 text-xl mb-4">
                Portals
              </h4>
              <ul className="space-y-3 text-gray-900">
                <li>
                  <a
                    href="#"
                    className="hover:text-orange-500 transition-transform duration-300 hover:translate-x-1 inline-block"
                  >
                    SaffronPages
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-orange-500 transition-transform duration-300 hover:translate-x-1 inline-block"
                  >
                    The Hindu Today
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-cinzel text-orange-500 text-xl mb-4">
                Resources
              </h4>
              <ul className="space-y-3 text-gray-900">
                <li>
                  <a
                    href="#"
                    className="hover:text-orange-500 transition-transform duration-300 hover:translate-x-1 inline-block"
                  >
                    Sacred Texts
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-orange-500 transition-transform duration-300 hover:translate-x-1 inline-block"
                  >
                    Festivals
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-cinzel text-orange-500 text-xl mb-4">
                Community
              </h4>
              <ul className="space-y-3 text-gray-900">
                <li>
                  <a
                    href="#"
                    className="hover:text-orange-500 transition-transform duration-300 hover:translate-x-1 inline-block"
                  >
                    Forums
                  </a>
                </li>
                <li>
                  <a
                    href="#"
                    className="hover:text-orange-500 transition-transform duration-300 hover:translate-x-1 inline-block"
                  >
                    Volunteer
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-cinzel text-orange-500 text-xl mb-4">
                Connect
              </h4>
              <div className="flex gap-4 text-2xl mt-4">
                <a
                  href="#"
                  className="text-gray-900 hover:text-orange-500 transition-transform duration-300 hover:-translate-y-1"
                >
                  <i className="fab fa-facebook-f"></i>
                </a>
                <a
                  href="#"
                  className="text-gray-900 hover:text-orange-500 transition-transform duration-300 hover:-translate-y-1"
                >
                  <i className="fab fa-instagram"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="py-8 border-t border-orange-500/20 text-center text-sm text-white/80 bg-gradient-to-br from-[#2c1810] to-[#1a0f08]">
        <p>
          &copy; {new Date().getFullYear()} {companyName}. Spreading divine
          wisdom across the digital realm.
        </p>
      </div>
    </footer>
  );
};

function Home() {
  return (
    <div
      className=" select-none"
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
    >
      <div>
        <HeroSlider />
        {/* <LocationSEO /> */}
        <AdvancedCards />
        <About />
        <Team />
        <Footer />
      </div>

      <style>
        {`
     
.advance-scrollbar::-webkit-scrollbar {
  width: 4px;
}
.advance-scrollbar::-webkit-scrollbar-track {
  background: rgba(139, 0, 0, 0.05);
  border-radius: 9999px;
}
.advance-scrollbar::-webkit-scrollbar-thumb {
  background: linear-gradient(to bottom, #991b1b, #f97316);
  border-radius: 9999px;
}
.advance-scrollbar::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(to bottom, #FF6B35, #DAA520);
}

/* Firefox */
.advance-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #f97316 rgba(139, 0, 0, 0.05);
}
        `}
      </style>
    </div>
  );
}

export default Home;
